/*
 * PurrEase Calm Plan: "Crafting your Calm Plan" screen + plan reveal.
 * Markup and styles: snippets/purrease-calm-reveal.liquid.
 *
 * assets/purrease-calm-plan.js calls window.PECalmReveal.open(selection, options) when a
 * shopper presses Start My Calm Plan. This file only presents the plan; it never charges.
 * The Claim button calls options.onClaim(button), which runs the existing cart -> checkout.
 *
 * Every number shown comes from the selection the page passes in (live Shopify/Appstle data):
 *   rooms, intro (first-box price), recurring (monthly price), diffuser (regular price each),
 *   ship (shipping per box, 0 when free), min (box minimum), catName (optional), termsUrl.
 */
(function () {
  'use strict';

  var root = document.getElementById('pe-cr');
  if (!root) return;
  if (root.parentNode !== document.body) document.body.appendChild(root);

  function $(id) {
    return document.getElementById(id);
  }

  var el = {
    craft: $('pe-cr-craft'),
    reveal: $('pe-cr-reveal'),
    steps: $('pe-cr-steps'),
    pct: $('pe-cr-pct'),
    prog: $('pe-cr-prog'),
    live: $('pe-cr-live'),
    verb: $('pe-cr-verb'),
    who: $('pe-cr-who'),
    kicker: $('pe-cr-kicker'),
    skip: $('pe-cr-skip'),
    close: $('pe-cr-x'),
    claim: $('pe-cr-claim'),
    adjust: $('pe-cr-adjust'),
    warn: $('pe-cr-warn'),
    motes: $('pe-cr-motes'),
    heading: $('pe-cr-h'),
    stamp: $('pe-cr-stamp'),
    tl: $('pe-cr-tl'),
    total: $('pe-cr-total'),
  };

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var RING = 515.2; // 2 * PI * r (r = 82)
  var state = {
    open: false,
    timers: [],
    raf: 0,
    sel: null,
    opts: null,
    returnFocus: null,
    runs: 0,
    pushed: false,
    revealed: false,
  };

  /* ---------- helpers ---------- */

  function reduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function later(fn, ms) {
    var id = setTimeout(fn, ms);
    state.timers.push(id);
    return id;
  }

  function stopAll() {
    state.timers.forEach(clearTimeout);
    state.timers = [];
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = 0;
  }

  function setText(id, text) {
    var node = $(id);
    if (node) node.textContent = text;
  }

  function track(name, rooms) {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', name);
        if (rooms) window.clarity('set', 'cp_rooms', String(rooms));
      }
    } catch (error) {
      /* analytics is optional */
    }
  }

  function money(cents) {
    var fmt = state.opts && state.opts.money;
    return fmt ? fmt(cents) : '$' + (cents / 100).toFixed(2);
  }

  function plural(n, word, many) {
    return n + ' ' + (n === 1 ? word : many || word + 's');
  }

  function possessive(name) {
    return name + (/s$/i.test(name) ? '’' : '’s');
  }

  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    while (days > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) days--;
    }
    return d;
  }

  function addMonths(date, months) {
    var d = new Date(date.getTime());
    var day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    var last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
    return d;
  }

  function fmtDate(date, withWeekday) {
    try {
      return date.toLocaleDateString('en-US', withWeekday ? { weekday: 'short', month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric' });
    } catch (error) {
      return date.toDateString();
    }
  }

  function fmtTime(date) {
    try {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  }

  function easeInOut(t) {
    return 0.5 - Math.cos(Math.PI * t) / 2;
  }

  /* ---------- background motes (built once) ---------- */

  var motesBuilt = false;
  function buildMotes() {
    if (motesBuilt || !el.motes || reduced()) return;
    motesBuilt = true;
    var html = '';
    for (var i = 0; i < 26; i++) {
      var size = (1.4 + Math.random() * 2.4).toFixed(1);
      var duration = (9 + Math.random() * 10).toFixed(1);
      html +=
        '<span class="pe-cr-mote" style="--x:' + (Math.random() * 100).toFixed(1) + '%;--s:' + size + 'px;--d:' + duration +
        's;--dl:-' + (Math.random() * duration).toFixed(1) + 's;--o:' + (0.25 + Math.random() * 0.55).toFixed(2) +
        ';--dx:' + (Math.random() * 80 - 40).toFixed(0) + 'px"></span>';
    }
    el.motes.innerHTML = html;
  }

  /* ---------- crafting sequence ---------- */

  function buildSteps(sel, shipBy) {
    var rooms = sel.rooms;
    var name = sel.catName;
    var steps = [
      {
        t: 'Mapping ' + plural(rooms, 'room'),
        s: rooms === 1 ? 'Her favorite room, covered' : rooms === 2 ? 'Living room + bedroom' : 'Three rooms — the whole home',
      },
      {
        t: 'Pairing ' + (rooms === 1 ? 'her PurrEase diffuser' : rooms + ' PurrEase diffusers'),
        s: 'One per room · up to 700 sq ft each',
      },
      {
        t: 'Setting ' + (name ? possessive(name) : 'her') + ' refill rhythm',
        s: 'A fresh 48 ml vial ' + (rooms > 1 ? 'for every room ' : '') + 'each month',
      },
      { t: 'Scheduling your first box', s: 'Ships by ' + fmtDate(shipBy, true) + ' · tracked' },
      { t: 'Adding something special for ' + (name || 'her') + '…', s: 'This part’s a surprise', gift: true },
    ];
    el.steps.innerHTML = '';
    return steps.map(function (step) {
      var li = document.createElement('li');
      li.className = 'pe-cr-step' + (step.gift ? ' pe-cr-step--gift' : '');
      var ico = document.createElement('span');
      ico.className = 'pe-cr-ico';
      ico.setAttribute('aria-hidden', 'true');
      var body = document.createElement('span');
      body.appendChild(document.createTextNode(step.t));
      var small = document.createElement('small');
      small.textContent = step.s;
      body.appendChild(small);
      li.appendChild(ico);
      li.appendChild(body);
      el.steps.appendChild(li);
      return li;
    });
  }

  function animateProgress(from, to, start, duration) {
    function frame(now) {
      var t = Math.min(1, Math.max(0, (now - start) / duration));
      var value = from + (to - from) * easeInOut(t);
      if (el.prog) el.prog.style.strokeDashoffset = String(RING * (1 - value));
      if (el.pct) el.pct.textContent = Math.round(value * 100) + '%';
      if (t < 1) state.raf = requestAnimationFrame(frame);
    }
    state.raf = requestAnimationFrame(frame);
  }

  function runCrafting(sel, quick, repeat) {
    var now = new Date();
    var shipBy = addBusinessDays(now, 2);
    var name = sel.catName;
    var total = quick ? 2200 : Math.max(4000, Math.min(9000, (sel.craftSeconds || 7) * 1000));
    var intro = quick ? 300 : 700;
    var outro = quick ? 300 : 600;
    var stepTime = (total - intro - outro) / 5;

    if (el.verb) el.verb.textContent = repeat ? 'Updating' : 'Crafting';
    if (el.who) el.who.textContent = name ? possessive(name) : 'your';
    if (el.kicker) el.kicker.textContent = name ? 'Built around the rooms ' + name + ' lives in.' : 'Built around the rooms she lives in.';
    if (el.live) el.live.textContent = 'Crafting live · ' + fmtTime(now);
    if (el.prog) el.prog.style.strokeDashoffset = String(RING);
    if (el.pct) el.pct.textContent = '0%';

    var items = buildSteps(sel, shipBy);
    animateProgress(0, 1, performance.now() + intro, total - intro - outro);

    items.forEach(function (li, i) {
      later(function () {
        li.classList.add('is-on');
        if (i === items.length - 1) root.classList.add('pe-cr--gift');
      }, intro + i * stepTime);
      later(function () {
        li.classList.remove('is-on');
        li.classList.add('is-done');
      }, intro + (i + 1) * stepTime - (quick ? 60 : 140));
    });

    if (!quick && el.skip) later(function () { el.skip.classList.add('is-on'); }, 2600);
    later(function () { showReveal(sel, now, shipBy); }, total);
  }

  /* ---------- reveal ---------- */

  function tickTotal(from, to, delay) {
    if (!el.total) return;
    el.total.textContent = money(from);
    if (reduced() || from <= to) {
      el.total.textContent = money(to);
      return;
    }
    later(function () {
      var start = performance.now();
      var duration = 900;
      function frame(now) {
        var t = Math.min(1, (now - start) / duration);
        var value = Math.round(from + (to - from) * (1 - Math.pow(1 - t, 3)));
        el.total.textContent = money(t >= 1 ? to : value);
        if (t < 1) state.raf = requestAnimationFrame(frame);
      }
      state.raf = requestAnimationFrame(frame);
    }, delay);
  }

  function fillReveal(sel, now, shipBy) {
    var rooms = sel.rooms;
    var name = sel.catName;
    var diffusers = rooms * sel.diffuser;
    var ship = sel.ship || 0;
    var today = sel.intro + ship;
    var monthly = sel.recurring + ship;
    var hasIntro = sel.intro < sel.recurring;
    var saving = diffusers + (hasIntro ? sel.recurring - sel.intro : 0);
    var min = sel.min || 3;

    setText('pe-cr-stamp', 'Crafted live · ' + fmtTime(now));
    setText('pe-cr-h', (name ? possessive(name) : 'Your') + ' Calm Plan is ready');
    setText('pe-cr-gift-t', rooms === 1 ? 'Your diffuser is on us.' : 'Your ' + rooms + ' diffusers are on us.');
    setText('pe-cr-gift-was', money(diffusers));
    setText('pe-cr-gift-n', rooms === 1 ? 'Yours to keep' : 'One for every room · yours to keep');

    setText('pe-cr-r1-l', rooms === 1 ? 'PurrEase diffuser' : rooms + ' PurrEase diffusers');
    setText('pe-cr-r1-was', money(diffusers));
    setText('pe-cr-r2-l', 'First box · ' + (rooms === 1 ? '30-day refill' : rooms + ' × 30-day refills'));
    setText('pe-cr-r2-was', hasIntro ? money(sel.recurring) : '');
    setText('pe-cr-r2-p', money(sel.intro));
    var tag = root.querySelector('.pe-cr-tag');
    if (tag) tag.style.display = hasIntro ? '' : 'none';
    var shipCell = $('pe-cr-r3-p');
    if (shipCell) {
      shipCell.textContent = ship ? money(ship) : 'FREE';
      shipCell.className = ship ? '' : 'pe-cr-free';
    }
    setText('pe-cr-save', 'You save ' + money(saving) + ' today');

    setText('pe-cr-then-t', 'Then ' + money(sel.recurring) + ' a month' + (ship ? ' + ' + money(ship) + ' shipping' : ''));
    setText(
      'pe-cr-then-n',
      (rooms === 1 ? 'A fresh vial' : rooms + ' fresh vials') + ', delivered before the last runs dry' + (ship ? '' : ' · free priority shipping')
    );

    if (el.tl) {
      var nodes = [];
      var shown = Math.min(min, 3);
      for (var b = 1; b <= shown; b++) {
        var when = b === 1 ? 'Today' : 'est. ' + fmtDate(addMonths(now, b - 1), false);
        nodes.push('<li><b>Box ' + b + '</b>' + when + '<em>' + money(b === 1 ? today : monthly) + '</em></li>');
      }
      nodes.push('<li><b>Then</b>month to month<em>Skip · pause · cancel</em></li>');
      el.tl.innerHTML = nodes.join('');
      el.tl.style.gridTemplateColumns = 'repeat(' + nodes.length + ',1fr)';
    }

    var termsLink = sel.termsUrl ? ' <a href="' + sel.termsUrl + '" target="_blank" rel="noopener">Calm Plan Terms</a>.' : '';
    var terms = $('pe-cr-terms');
    if (terms) {
      terms.innerHTML =
        'The Calm Plan is a subscription. Today you pay ' + money(today) + ' for your first box. It then renews automatically every month at ' +
        money(monthly) + (ship ? ' (' + money(sel.recurring) + ' + ' + money(ship) + ' shipping)' : '') + ' until you cancel. ' +
        min + '-box minimum: your first box + ' + (min - 1) + ' monthly refills; after box ' + min +
        ', skip, pause or cancel anytime from your account. We email you 3 days before every charge. If a plan ends before box ' + min +
        ', the diffuser' + (rooms > 1 ? 's’' : '’s') + ' regular price (' + money(diffusers) + ') applies unless returned unused within 14 days.' +
        termsLink;
    }
    var key = $('pe-cr-key');
    if (key) {
      key.innerHTML = '<b>' + money(today) + ' today</b>, then ' + money(monthly) + '/mo · renews monthly · ' + min + '-box minimum';
    }
    if (el.claim) {
      el.claim.textContent = (name ? 'Claim ' + possessive(name) + ' plan' : 'Claim my Calm Plan') + ' — ' + money(today) + ' →';
      el.claim.disabled = false;
      el.claim.removeAttribute('aria-busy');
    }
    hideWarn();
    tickTotal(diffusers + sel.recurring + ship, today, 900);
  }

  function syncMore() {
    if (!el.reveal) return;
    var more = el.reveal.scrollHeight - el.reveal.clientHeight - el.reveal.scrollTop > 6;
    root.classList.toggle('pe-cr--more', more);
  }
  if (el.reveal) {
    el.reveal.addEventListener('scroll', syncMore, { passive: true });
    window.addEventListener('resize', function () {
      if (state.open) syncMore();
    });
  }

  function showReveal(sel, now, shipBy) {
    if (!state.open || state.revealed) return;
    state.revealed = true;
    if (el.skip) el.skip.classList.remove('is-on');
    if (el.prog) el.prog.style.strokeDashoffset = '0';
    if (el.pct) el.pct.textContent = '100%';
    fillReveal(sel, now || new Date(), shipBy);
    el.reveal.hidden = false;
    if (el.reveal) el.reveal.scrollTop = 0;
    root.classList.add('pe-cr--gift');
    void el.reveal.offsetWidth;
    root.classList.add('pe-cr--reveal');
    syncMore();
    later(syncMore, 1200);
    track('cp_reveal', sel.rooms);
    later(function () {
      if (el.heading) el.heading.focus({ preventScroll: true });
    }, reduced() ? 0 : 450);
  }

  /* ---------- open / close ---------- */

  function open(sel, opts) {
    if (!sel) return;
    stopAll();
    state.sel = sel;
    state.opts = opts || {};
    state.returnFocus = state.opts.returnFocus || document.activeElement;
    state.open = true;
    state.revealed = false;
    // First plan of the visit gets the full crafting screen; repeats (and reduced motion) get ~2s.
    var repeat = state.runs > 0;
    try {
      if (!repeat && window.sessionStorage && sessionStorage.getItem('pe_cr_seen') === '1') repeat = true;
      if (window.sessionStorage) sessionStorage.setItem('pe_cr_seen', '1');
    } catch (error) {
      /* storage blocked: keep the full experience */
    }
    var quick = repeat || reduced();
    state.runs++;

    root.classList.remove('pe-cr--reveal', 'pe-cr--gift', 'pe-cr--in');
    if (el.reveal) el.reveal.hidden = true;
    if (el.skip) el.skip.classList.remove('is-on');
    buildMotes();
    root.hidden = false;
    document.documentElement.classList.add('pe-cr-lock');
    void root.offsetWidth;
    root.classList.add('pe-cr--in');
    root.focus({ preventScroll: true });

    if (!state.pushed) {
      try {
        history.pushState({ peCalmReveal: true }, '');
        state.pushed = true;
      } catch (error) {
        state.pushed = false;
      }
    }
    track('cp_craft_start', sel.rooms);
    runCrafting(sel, quick, repeat);
  }

  function close(fromHistory) {
    if (!state.open) return;
    state.open = false;
    stopAll();
    root.classList.remove('pe-cr--in');
    document.documentElement.classList.remove('pe-cr-lock');
    var hideAfter = reduced() ? 0 : 380;
    setTimeout(function () {
      if (state.open) return;
      root.hidden = true;
      root.classList.remove('pe-cr--reveal', 'pe-cr--gift');
      if (el.reveal) el.reveal.hidden = true;
    }, hideAfter);
    if (state.pushed && !fromHistory) {
      state.pushed = false;
      try {
        history.back();
      } catch (error) {
        /* ignore */
      }
    }
    if (fromHistory) state.pushed = false;
    var target = state.returnFocus;
    if (target && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }
    track('cp_adjust');
  }

  function showWarn(message) {
    if (!el.warn) return;
    el.warn.textContent = message || 'That plan isn’t available right now. Please try another room count.';
    el.warn.classList.add('is-on');
  }

  function hideWarn() {
    if (!el.warn) return;
    el.warn.textContent = '';
    el.warn.classList.remove('is-on');
  }

  /* ---------- wiring ---------- */

  if (el.close) el.close.addEventListener('click', function () { close(false); });
  if (el.adjust) el.adjust.addEventListener('click', function () { close(false); });
  if (el.skip) {
    el.skip.addEventListener('click', function () {
      stopAll();
      showReveal(state.sel, new Date(), addBusinessDays(new Date(), 2));
    });
  }
  if (el.claim) {
    el.claim.addEventListener('click', function () {
      if (!state.opts || typeof state.opts.onClaim !== 'function' || el.claim.disabled) return;
      hideWarn();
      track('cp_claim', state.sel && state.sel.rooms);
      var result = state.opts.onClaim(el.claim);
      if (result && typeof result.catch === 'function') {
        result.catch(function (error) {
          showWarn(error && error.message);
        });
      }
    });
  }

  root.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(false);
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.filter.call(
      root.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])'),
      function (node) {
        return !node.disabled && node.offsetParent !== null && !node.closest('[hidden]');
      }
    );
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('popstate', function () {
    if (state.open) close(true);
  });

  // Coming back from checkout with the back button restores this page from cache:
  // re-enable the Claim button so the plan can be claimed again.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || !el.claim) return;
    el.claim.disabled = false;
    el.claim.removeAttribute('aria-busy');
    if (state.sel) fillReveal(state.sel, new Date(), addBusinessDays(new Date(), 2));
  });

  window.PECalmReveal = {
    open: open,
    close: function () { close(false); },
    isOpen: function () { return state.open; },
  };
})();